import React, { useEffect, useState } from "react";
import {
  Toolbar,
  IconButton,
  Box,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Divider,
  Modal,
  Button,
  Grid,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationImportant from "@mui/icons-material/NotificationImportant";
import MenuIcon from "@mui/icons-material/Menu";
import { StyledAppBar, StyledMenuButton } from "./NavBar.styles";
import axios from "axios";

interface NavBarProps {
  toggleSideBar: () => void;
}

export const NavBar: React.FC<NavBarProps> = ({ toggleSideBar }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const open = Boolean(anchorEl);

  const userDataString = localStorage.getItem("userData");
  const userData = userDataString ? JSON.parse(userDataString) : null;

  const token = localStorage.getItem("token") || "";

  useEffect(() => {
    const fetchConfiguracoesUsuario = async () => {
      try {
        const url = `${process.env.REACT_APP_API_DOMAIN}/entrega/config`;
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        };
        const response = await axios.get(url, config);
        localStorage.setItem("configUsuario", JSON.stringify(response.data));
      } catch (error) {
        console.error("Erro ao buscar configurações do usuário:", error);
      }
    };

    if (token) {
      fetchConfiguracoesUsuario();
    }
  }, []);

  const fetchNotifications = async () => {
    try {
      const url = `${process.env.REACT_APP_API_DOMAIN}/manager/notificacao`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      };
      const response = await axios.get(url, config);
      setNotifications(response.data || []);
      const unreadCount = response.data.filter(
        (n: any) => !n.lida_notificao
      ).length;
      setNotificationCount(unreadCount);
    } catch (error) {
      console.error("Erro ao buscar notificações:", error);
    }
  };

  const markNotificationAsRead = async (notificationId: string) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_DOMAIN}/manager/notificacao/${notificationId}`,
        { lida_notificao: true },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        }
      );
      return true;
    } catch (error) {
      console.error("Erro ao marcar notificação como lida:", error);
      return false;
    }
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationItemClick = (notification: any) => {
    setSelectedNotification(notification);
    setIsModalOpen(true);
    setAnchorEl(null);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedNotification(null);
  };

  const handleMarkAsRead = async () => {
    if (!selectedNotification) return;
    const success = await markNotificationAsRead(selectedNotification.id);
    if (success) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === selectedNotification.id ? { ...n, lida_notificao: true } : n
        )
      );
      setNotificationCount((prev) => Math.max(prev - 1, 0));
      setIsModalOpen(false);
      setSelectedNotification(null);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  return (
    <StyledAppBar position="fixed" sx={{ zIndex: 1300 }}>
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <StyledMenuButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={toggleSideBar}
          sx={{ flexShrink: 0 }}
        >
          <MenuIcon />
        </StyledMenuButton>

        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
          <img
            src={userData ? userData.logo_empresa : "../../logoStaff.png"}
            alt="Logo da empresa"
            style={{
              maxWidth: "100%",
              maxHeight: 60,
              height: "auto",
              display: "block",
              paddingTop: 5,
              paddingBottom: 5,
            }}
          />
        </Box>

        <IconButton
          color="inherit"
          aria-label="notificações"
          sx={{ flexShrink: 0 }}
          onClick={handleNotificationClick}
        >
          <Badge badgeContent={notificationCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>

        <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
          {notifications.filter((n) => !n.lida_notificao).length > 0 ? (
            notifications
              .filter((notification) => !notification.lida_notificao)
              .map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationItemClick(notification)}
                  sx={{ whiteSpace: "normal", width: { xs: "100%", sm: 400 } }}
                >
                  <ListItemIcon>
                    <NotificationImportant fontSize="medium" />
                  </ListItemIcon>
                  <Typography component={"span"}>
                    {notification.menssagem_notificacao
                      .split("\n")
                      .map((line: string, index: number) => (
                        <React.Fragment key={index}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    <Divider sx={{ mt: 1 }} variant="middle" />
                  </Typography>
                </MenuItem>
              ))
          ) : (
            <MenuItem disabled>Nenhuma notificação disponível</MenuItem>
          )}
        </Menu>

        <Modal open={isModalOpen} onClose={handleModalClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
              width: "80%",
              maxWidth: 400,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Deseja marcar a notificação como lida?
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 3, whiteSpace: "pre-line", textAlign: "left" }}
            >
              {selectedNotification?.menssagem_notificacao}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleMarkAsRead}
                >
                  Confirmar
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button variant="outlined" fullWidth onClick={handleModalClose}>
                  Cancelar
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Modal>
      </Toolbar>
    </StyledAppBar>
  );
};
